import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive, PopularLazyLoadSelector } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import EmptyScreensNotificationsComponent from 'webapp/components/empty-screens/notifications';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import NotificationsTableRowComponent from 'webapp/components/notifications/table-row';
import TableSorterComponent from 'webapp/components/table-sorter';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type { Notification } from 'webapp/models-custom';
import type { DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortNotification from 'webapp/models-custom/filter-sort/notification';

type Args = object;

export default class NotificationsTableComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/alerts')
	declare mmAlertsService: Service.ModelManager.Alerts;

	filterSort: FilterSortNotification;
	batchSelect: BatchSelect<Notification>;
	addButtonText = 'New notification';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortNotification({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.allItems;
			},
		});

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});
	}

	get addButtonContainerNode() {
		return isAlive(this) && !this.selectedItems.length
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): Notification[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<DeleteAction>[] {
		return [this.batchSelect.deleteOption];
	}

	@cached
	get allTargetModels() {
		return this.mmNotificationsService.getAllModelsWithPossibleNotifications();
	}

	private _allItems: Notification[] = [];

	@cached
	get allItems() {
		this.mmNotificationsService.updateModelCache(this._allItems, this.allTargetModels);
		return this._allItems.filter((i) => i.isValidForList);
	}

	get showList() {
		return this.allItems.length;
	}

	doBatchAction(actionName: DeleteAction) {
		return this[actionName]();
	}

	@action
	openNewNotificationOptions() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmNotificationsService.startCreateFlow('notifications');
	}

	@action
	batchDelete() {
		const items = this.selectedItems;

		const promise = new Promise<void>((resolve, reject) => {
			let wasSubmitted = false;
			const affirmativeFunc = () => {
				wasSubmitted = true;
				items.forEach((item) => {
					item.delete();
				});
				resolve();
			};

			const popup = this.mmAlertsService.openBatchDeleteConfirmationPopup(
				affirmativeFunc,
				items.length,
				undefined,
				'Notification',
			);

			popup.on(PopupEvent.CLOSING, this, () => {
				if (!wasSubmitted) {
					reject();
				}
			});
		});

		void promise.then(() => {
			this.infolayerService.ok('Notifications deleted!');
		});

		return promise;
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		{{#if this.showList}}

			<div class="bottom-padding">

				<FilterLineProxyComponent @model={{this.filterSort}} />

				<FilterNoResultsComponent @model={{this.filterSort}} />

				<CommonListLazyLoadComponent
					@filterSort={{this.filterSort}}
					@initialRowCount={{10}}
					@loadMoreRowCount={{5}}
					@animationProperty="id"
					@autoLoaded={{true}}
					@bottomDistance={{700}}
					as |visibleItems exporter|
				>

					<table
						id="notifications-list"
						class={{joinClasses
							(array
								"bigtable"
								"flexible-table"
								"has-avatar"
								"has-checkbox"
								(if this.filterSort.noResults "hide")
							)
						}}
						{{visibleCheckContainer}}
						{{didUpdate this.triggerContentChange visibleItems}}
					>
						<colgroup>
							<col class="cc" />
							<col class="h0" />
							<col width="30%" />
							<col />
							<col />
							<col />
							<col />
							<col class="button-ct" />
						</colgroup>
						<thead>
							<tr>
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
								<th class="h0">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="type"
										@name="Type"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th>
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="title"
										@name="Name"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="h2 prio-medium">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="frequencySortProp"
										@name="Cadence"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="prio-low">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="deliveryTypes.length"
										@name="Delivery"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="prio-low">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="allRecipients.length"
										@name="Recipients"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="prio-medium">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="toggleSortProp"
										@name="Active for me"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="button-ct"></th>
							</tr>
						</thead>
						<tbody>
							{{#each visibleItems as |item|}}
								<NotificationsTableRowComponent
									@model={{item}}
									@batchSelect={{this.batchSelect}}
								/>
							{{/each}}
						</tbody>
					</table>

					{{#unless exporter.areAllItemsLoaded}}
						<CommonListLoaderComponent />
					{{/unless}}

				</CommonListLazyLoadComponent>

				<TableAddNewItemComponent
					@title={{this.addButtonText}}
					@addNewItem={{this.openNewNotificationOptions}}
					@model={{this.filterSort}}
				/>
			</div>

			{{#if this.addButtonContainerNode}}
				{{#in-element this.addButtonContainerNode}}
					<CommonAddButtonComponent
						@text={{this.addButtonText}}
						@callTrigger={{this.openNewNotificationOptions}}
					/>
				{{/in-element}}
			{{/if}}

			{{#if this.batchButtonContainerNode}}
				{{#in-element this.batchButtonContainerNode}}
					<CommonBatchButtonComponent @model={{this.batchSelect}} />
				{{/in-element}}
			{{/if}}

		{{else}}

			<EmptyScreensNotificationsComponent @onCreate={{this.openNewNotificationOptions}} />

		{{/if}}
	</template>
}
